import {
  ConfirmDialog,
  Div,
  Flex,
  LoadingSpinner,
  Text,
} from '@sendoutcards/quantum-design-ui'
import React, { FC } from 'react'
import { useActions, useSelector, useState } from 'src/hooks'
import { AddOrderCardRoute } from 'src/orders/routes/AddOrderCardRoute'
import { Welcome } from 'src/dashboard/components/Widgets/Reminders/components/Welcome'
import { WeekDayView } from 'src/dashboard/components/Widgets/Reminders/components/WeekDayView'
import { Widget } from 'src/dashboard/components/Widgets/Widget'
import { ReminderListCard } from 'src/dashboard/components/Widgets/Reminders/components/ReminderListCard'
import { Button } from 'src/design_system/components/Button/Button'
import {
  useCreateOrder,
  useReminders,
  useUpdateReminder,
} from 'src/react_query'
import {
  DayInfoType,
  useGetCurrentWeekDays,
} from 'src/dashboard/hooks/useCurrentWeekDays'
import { MinimalReminderFragment } from 'src/graphql/generated/graphql'

type RemindersListWidgetProps = {
  firstName?: string
  isLandscape?: boolean
}

export const RemindersListWidget: FC<RemindersListWidgetProps> = ({
  firstName,
  isLandscape,
}) => {
  const [selectedReminder, setSelectedReminder] = useState<
    MinimalReminderFragment | undefined
  >()
  const actions = useActions()
  const createOrder = useCreateOrder()
  const updateReminder = useUpdateReminder()

  const windowWidth = useSelector(state => state.window.width)
  const isMobile = windowWidth <= 574
  const isWide = windowWidth >= 1260

  const currentWeekDays: DayInfoType[] = useGetCurrentWeekDays()
  const currentDay = currentWeekDays.find(day => day.isToday)

  const { data, isLoading } = useReminders({
    offset: 0,
    limit: 5,
  })

  const reminders = data?.reminders.filter(
    reminder => !(reminder.type === 'PROJECT' && !reminder.campaign),
  )

  const handleCreateOrder = async (
    contact: { id: string },
    reminderId: string,
  ) => {
    const {
      createOrder: { order },
    } = await createOrder.mutateAsync({
      order: { contacts: [contact.id] },
    })

    await updateReminder.mutateAsync({
      reminder: { id: reminderId, orderId: order.id },
    })
    actions.openOrder(order.id, AddOrderCardRoute())
  }
  const weekDayEvents = reminders?.map(reminder => reminder.eventDate ?? '')

  const getGreeting = (firstName: string | null | undefined): string => {
    const currentHour = new Date().getHours()
    let mutableGreeting

    if (currentHour < 12) {
      mutableGreeting = 'Good Morning'
    } else if (currentHour < 18) {
      mutableGreeting = 'Good Afternoon'
    } else {
      mutableGreeting = 'Good Evening'
    }

    return firstName ? `${mutableGreeting}, ${firstName}` : mutableGreeting
  }

  return (
    <Flex
      width="100%"
      flexDirection={isLandscape ? 'row' : 'column'}
      inset="x2"
      backgroundColor="foreground"
      height={isWide ? '786px' : undefined}
      style={{
        borderRadius: 32,
        boxShadow: 'rgba(0, 0, 0, 0.04) 0px 4px 32px',
      }}
      overflowY="hidden"
    >
      <Flex outset={{ right: isLandscape ? '24px' : undefined }}>
        <Welcome
          shouldStackGreeting={isLandscape || isMobile}
          isMobile={isMobile}
          backgroundColor="#d4e9ebd6"
          graphicUrl="https://soc-website-public.s3.us-west-2.amazonaws.com/static/soc-website/images/Card.png"
          title={getGreeting(firstName)}
          description={'Share some gratitude today!'}
          action={{
            title: 'Send a Card',
            onClick: () => actions.openCatalog(),
          }}
        />
      </Flex>
      <Flex
        flexDirection="column"
        inset={{ horizontal: 'x_75' }}
        overflowY={'scroll'}
        width="100%"
      >
        <Flex overflowY={'scroll'} width="100%">
          <WeekDayView weekDayEventList={weekDayEvents} isMobile={isMobile} />
        </Flex>
        <Text
          type="caption"
          weight="bold"
          color="primaryHeading"
          content="Upcoming Reminders"
          style={{ marginLeft: 4 }}
        />

        {isLoading ? (
          <Div style={{ marginTop: 16, marginBottom: 16 }}>
            <LoadingSpinner size="large" />
          </Div>
        ) : !reminders?.length ? (
          <Widget
            shouldHideShadow={true}
            backgroundColor="#F8F9FA"
            isEmpty={true}
            emptyState={{
              title: `No ${currentDay?.month} Reminders`,
              description:
                'Birthdays and Anniversaries can be added to each of your contacts so you never forget a special occasion again!',
              icon: 'exp_calendar',
              action: {
                title: 'Add a reminder',
                onClick: () => alert('Fired from RemindersListWidget.tsx'),
                minWidth: '160px',
              },
            }}
          />
        ) : (
          reminders.slice(0, 5).map(reminder => {
            const name = `${reminder.contact.firstName} ${reminder.contact.lastName}`
            const type = reminder.type.toLowerCase()
            return (
              <ReminderListCard
                key={reminder.id}
                type={type}
                name={name}
                date={reminder.eventDate ?? ''}
                onClick={() => setSelectedReminder(reminder)}
              />
            )
          })
        )}
        {/* TODO: Refactor this to have the dateBadge in the top center */}
        {selectedReminder && (
          <ConfirmDialog
            isOpen={!!selectedReminder}
            onClose={() => setSelectedReminder(undefined)}
            title={`It's Time to Celebrate ${
              selectedReminder.contact.firstName
            }'s ${
              selectedReminder.type.charAt(0).toUpperCase() +
              selectedReminder.type.slice(1).toLowerCase()
            }!`}
            description="Get creative and fun sending a card and gift that will surely make them feel special."
            primaryAction="accept"
            accept={{
              title: 'Create Card',
              onClick: () =>
                handleCreateOrder(
                  selectedReminder.contact,
                  selectedReminder.id,
                ),
            }}
            decline={{
              title: 'Send Later',
              onClick: () => setSelectedReminder(undefined),
            }}
          >
            {/* <DateBadge
            type={selectedReminder.type.toLowerCase()}
            date={selectedReminder.eventDate ?? ''}
          /> */}
          </ConfirmDialog>
        )}
        <Flex width="100%" justifyContent="center">
          <Button
            title="View All Reminders"
            textSize="12px"
            background="#be9cdd"
            minWidth="160px"
            onClick={() => actions.openNotifications()}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}
